import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { analytics } from "@/lib/analytics";

export function Hero() {
  const [stats, setStats] = useState({});

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const scroll = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative bg-white overflow-hidden pt-[52px]">
      {/* Very subtle top-center radial — Apple product page feel */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[600px]"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% -5%, rgba(0,113,227,0.07) 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-[980px] mx-auto px-4 sm:px-6 text-center py-24 sm:py-32 lg:py-40">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {/* Eyebrow */}
          <p
            className="text-[13px] font-semibold tracking-[0.08em] uppercase text-[#6e6e73] mb-4"
          >
            North Carolina · Indian Community
          </p>

          {/* Headline */}
          <h1
            className="text-[64px] sm:text-[80px] lg:text-[96px] font-bold text-[#1d1d1f] mb-6"
            style={{ letterSpacing: "-0.04em", lineHeight: "1.04" }}
          >
            From Stranger
            <br />
            to Family.
          </h1>

          {/* Subhead */}
          <p className="text-[19px] sm:text-[21px] text-[#6e6e73] max-w-[600px] mx-auto leading-[1.52] mb-10" style={{ letterSpacing: "-0.01em" }}>
            North Carolina has hundreds of thousands of Indians. Until now, we had no place to truly find each other.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => { scroll("community-board"); analytics.clickExplore(); }}
              className="inline-flex items-center justify-center bg-[#0071e3] hover:bg-[#0077ed] active:bg-[#006edb] text-white text-[17px] font-medium px-8 py-[14px] rounded-[980px] transition-colors duration-150 min-w-[220px]"
            >
              Explore the community
            </button>
            <button
              onClick={() => { scroll("newsletter"); analytics.clickJoinFree(); }}
              className="inline-flex items-center justify-center text-[#0071e3] hover:text-[#0077ed] text-[17px] font-medium transition-colors duration-150"
            >
              Join free <span className="ml-1 text-[20px] leading-none">›</span>
            </button>
          </div>
        </motion.div>
      </div>

      {/* Stats bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.6 }}
        className="border-t border-[#d2d2d7]"
      >
        <div className="max-w-[980px] mx-auto px-4 sm:px-6">
          <div className="flex divide-x divide-[#d2d2d7]">
            <div className="flex-1 py-6 text-center">
              <p className="text-[28px] font-semibold text-[#1d1d1f]" style={{ letterSpacing: "-0.02em" }}>
                {stats.members ? stats.members.toLocaleString() + '+' : '—'}
              </p>
              <p className="text-[14px] text-[#6e6e73] mt-1">Members</p>
            </div>
            <div className="flex-1 py-6 text-center">
              <p className="text-[28px] font-semibold text-[#1d1d1f]" style={{ letterSpacing: "-0.02em" }}>
                100%
              </p>
              <p className="text-[14px] text-[#6e6e73] mt-1">Free · Always</p>
            </div>
            <div className="flex-1 py-6 text-center">
              <p className="text-[28px] font-semibold text-[#1d1d1f]" style={{ letterSpacing: "-0.02em" }}>
                All NC
              </p>
              <p className="text-[14px] text-[#6e6e73] mt-1">Statewide coverage</p>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
